import { Token } from "agg-swap-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { wrapperResult } from "@api/osp";
import { QUERY_KEYS } from "../config";
import { OpenDataClient, TokenPagination } from "../features/api/openData";
import { rootStore } from "../store";
import { useOspApiClientProvider } from "./useOspApiClientProvider";

type TokensRankingQueryParams = NonNullable<
  Parameters<OpenDataClient<unknown>["tokens"]["getTokensRanking"]>[0]
>;

interface TokensRankingParams {
  queryParams?: TokensRankingQueryParams;
  enabled?: boolean;
}

const defaultLimit = 10;

const defaultQueryParams = {
  "enrich.with_market.enable": true,
  "enrich.enable": true,
  "enrich.with_rating": true,
  offset: 0,
  limit: defaultLimit,
};
export const useTokensRankingInfiniteScroll = (
  params?: TokensRankingParams,
) => {
  const { openDataApi } = useOspApiClientProvider();

  /**
   * 获取登录状态用于缓存键
   *
   * 问题分析：
   * - 未登录时请求了部分数据（如第1页）
   * - 登录后用户权限变化，可能看到不同的数据
   * - 需要清空缓存，重新从第0页开始请求
   *
   * 解决方案：
   * - 在 queryKey 中包含登录状态
   * - 登录状态变化时自动触发缓存失效
   * - 确保登录后重新从头开始加载数据
   */
  const {
    isLoggedIn,
    isLoading: isLoginLoading,
    isFetching: isLoginFetching,
  } = useSnapshot(rootStore.login);

  /**
   * 关键修复：包含登录状态的查询键
   *
   * 这确保了：
   * 1. 未登录时的缓存：[TOKENS_RANKING, params, false]
   * 2. 登录后的缓存：[TOKENS_RANKING, params, true]
   * 3. 两者是不同的缓存，登录时会自动重新请求
   */
  const queryKey = [
    QUERY_KEYS.TOKENS_RANKING,
    params?.queryParams,
    isLoggedIn, // 👈 关键：包含登录状态
  ];

  const initialPageParam = {
    ...defaultQueryParams,
    ...params?.queryParams,
  };

  return useInfiniteQuery({
    queryKey: queryKey,
    queryFn: async ({ pageParam }): Promise<TokenPagination> => {
      try {
        const response = await wrapperResult(
          openDataApi!.tokens.getTokensRanking(pageParam),
        );
        return response as TokenPagination;
      } catch (error) {
        throw new Error("Failed to fetch tokens ranking");
      }
    },
    getNextPageParam: (lastPage: any, _allPages: any, lastPageParam: any) => {
      if (
        lastPageParam?.limit === lastPage.rows?.length &&
        typeof lastPageParam?.offset === "number"
      ) {
        const nextParams = {
          ...lastPageParam,
          offset: lastPageParam?.offset + (lastPageParam.limit ?? defaultLimit),
        };
        return nextParams;
      }
      return undefined;
    },
    select: (data: any): Token[] | undefined => {
      return (
        data?.pages
          ?.flatMap((page: any) => page.rows || [])
          .filter((token: any): token is Token => !!token) || undefined
      );
    },
    initialPageParam: initialPageParam,
    /**
     * 启用条件优化：考虑登录状态稳定性
     *
     * 确保：
     * 1. API 客户端已就绪
     * 2. 登录状态稳定（不在加载中）
     * 3. 用户明确启用查询
     *
     * 这避免了登录状态变化过程中的重复请求
     */
    enabled:
      !!openDataApi &&
      !isLoginLoading &&
      !isLoginFetching &&
      (params?.enabled ?? true),
  });
};
